import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import colors from '../utils/colors';

const cardStyle = `
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  width: 100%;
  border: 0.1px solid ${colors.shade1()};
  border-radius: 2px;
  box-shadow: inset 0 0 0 1px ${colors.shade1()}, 0 5px 15px -5px rgba(0, 0, 0, 0.1);
  padding: 20px;
  text-decoration: none;
  background-color: #E7EEF4;
`;

const ProjectWrapperLink = styled.a`
  ${cardStyle}
  transition: transform 0.3s ease-out, box-shadow 0.3s ease-out;
  &:hover {
    transform: translateY(-4px);
    box-shadow: inset 0 0 0 1px ${colors.shade1()}, 0 12px 22px -10px rgba(0, 0, 0, 0.25);
  }
`;

// Not every project has somewhere to send the reader. Chaotic Era deliberately
// has no link until the forgery writeup is published.
const ProjectWrapperPlain = styled.div`
  ${cardStyle}
`;

const ProjectImage = styled.img`
  position: absolute;
  margin-left: 50%;
  height: 100%;
  transform: translateX(-50%);
  transition: transform 0.5s;
  max-width: 100%;
  text-decoration: none;
  object-fit: cover;
`;

const ProjectImageLink = styled.div`
  position: relative;
  display: block;
  width: 100%;
  padding-bottom: 56.25%;
  overflow: hidden;
  text-decoration: none;
  color: transparent;
  background: #dbe5ee;
`;

const VideoFrame = styled.div`
  position: relative;
  width: 100%;
  padding-bottom: 56.25%;
  overflow: hidden;

  iframe {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border: 0;
  }
`;

const ProjectTitle = styled.h1`
  text-transform: uppercase;
  text-align: center;
  font-family: 'Josefin Sans', sans-serif;
  font-size: 20px;
  text-decoration: none;
  margin: 5px;
  color: #015565;
`;

const ProjectDescription = styled.div`
  font-family: 'Open Sans', sans-serif;
  margin-top: 7px;
  text-align: left;
  text-decoration: none;
  color: #4E5D63;
  font-size: 14px;
  line-height: 23px;
  flex-grow: 1;

  p {
    margin: 0 0 10px 0;
  }

  p:last-child {
    margin-bottom: 0;
  }

  a {
    color: #015565;
    text-decoration: underline;

    &:hover {
      background-color: rgba(1, 85, 101, 0.08);
    }
  }
`;

const TagRow = styled.div`
  margin-top: auto;
  padding-top: 14px;
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const Tag = styled.span`
  display: inline-block;
  padding: 3px 10px;
  border-radius: 14px;
  font-size: 11px;
  font-family: 'Open Sans', sans-serif;
  background-color: #ffffff;
  color: #015565;
`;

const ProjectCard = (props) => {
  const Wrapper = props.url ? ProjectWrapperLink : ProjectWrapperPlain;
  const wrapperProps = props.url
    ? { href: props.url, target: '_blank', rel: 'noopener noreferrer' }
    : {};

  return (
    <Wrapper {...wrapperProps}>
      {props.video ? (
        <VideoFrame>
          <iframe
            src={props.video}
            title={props.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </VideoFrame>
      ) : null}
      {!props.video && props.src ? (
        <ProjectImageLink>
          <ProjectImage src={__PATH_PREFIX__ + props.src} alt="" />
        </ProjectImageLink>
      ) : null}
      <ProjectTitle>{props.title}</ProjectTitle>
      <ProjectDescription dangerouslySetInnerHTML={{ __html: props.description }} />
      {props.tags && props.tags.length ? (
        <TagRow>
          {props.tags.map((tag) => <Tag key={tag}>{tag}</Tag>)}
        </TagRow>
      ) : null}
    </Wrapper>
  );
};

ProjectCard.propTypes = {
  url: PropTypes.string,
  src: PropTypes.string,
  video: PropTypes.string,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  tags: PropTypes.arrayOf(PropTypes.string),
};

ProjectCard.defaultProps = {
  url: null,
  src: null,
  video: null,
  tags: [],
};

export default ProjectCard;
